import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TemplateSelector } from '../../components/template-selector/template-selector.component';
import { ApiService } from '../../../_services/api.service';
import { AuthService } from '../../../_services/auth.service';
import { Template } from '../../../_interfaces/template';
import { User } from '../../../_interfaces/user';
import { Dictation } from '../../../_interfaces/dictation';
import { CopilotDocument } from '../../../_interfaces/document';
import { CopilotProcessingService } from '../../../_services/copilot-processing.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { mapNoteSection } from '../../../_helpers/note-sections';
import { getNextDocumentPosition } from '../../../_helpers/utils';
import { hasDictationTranscription } from '../../../_helpers/transcription';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { APP_DIALOG_DATA } from '../../components/dialog/dialog.tokens';
import { AppDialogRef } from '../../components/dialog/dialog-ref';
import { DIALOG_IMPORTS } from '../../components/dialog/dialog.imports';

@Component({
  selector: 'app-select-template',
  standalone: true,
  imports: [
    CommonModule,
    ...DIALOG_IMPORTS,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TemplateSelector,
    MatIconModule,
    TranslateModule,
    LoadingSpinnerComponent
  ],
  templateUrl: './select-template.component.html',
})
export class SelectTemplateComponent implements OnInit {
  user: User;
  templates: Template[] = [];
  selectedTemplates: Template[] = [];
  preSelectedTemplates: Template[] = [];
  disabledTemplates: Template[] = [];
  isLoading = false;
  isSaving = false;
  dictation: Dictation | null = null;
  singleSelection = false;

  constructor(
    @Inject(APP_DIALOG_DATA) public data: {
      dictation?: Dictation;
      templates?: Template[];
      singleSelection?: boolean;
    },
    private dialogRef: AppDialogRef<SelectTemplateComponent>,
    private apiService: ApiService,
    private authService: AuthService,
    private processing: CopilotProcessingService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {
    this.user = this.authService.getUser() as User;
    this.dictation = this.data.dictation ?? null;
    this.singleSelection = this.data.singleSelection === true;
  }

  async ngOnInit() {
    if (this.data.templates) {
      this.processTemplates(this.data.templates);
    } else {
      await this.loadTemplates();
    }
  }

  private processTemplates(allTemplates: Template[]): void {
    const processedTemplates = this.dictation?.copilot?.documents?.processed ?? [];
    const hasProcessedTemplates = Array.isArray(processedTemplates) && processedTemplates.length > 0;

    if (!hasProcessedTemplates) {
      this.templates = allTemplates || [];
      this.disabledTemplates = [];
      const canPreselectDefaults = !this.singleSelection
        && !!this.user?.settings?.copilotTemplates
        && this.user.settings.copilotTemplates.length > 0;
      if (canPreselectDefaults) {
        const blueprintsEnabled = this.authService.isAiBlueprintsEnabled;
        const availableKeys = new Set<string>((this.user.settings.copilotTemplates || []).map(t => `${Number(t.id)}-${t.type}`));
        this.preSelectedTemplates = this.templates.filter(ct =>
          availableKeys.has(`${ct.id}-${ct.type}`) && (blueprintsEnabled || ct.type !== 'user')
        );
      } else {
        this.preSelectedTemplates = [];
      }

      this.selectedTemplates = [...this.preSelectedTemplates];
    } else {
      const existingKeys = new Set<string>(
        processedTemplates
          .filter(o => o && o.templateId !== undefined && o.templateType !== undefined)
          .map(o => `${o.templateId}-${o.templateType}`)
      );

      this.templates = allTemplates || [];
      this.disabledTemplates = this.templates.filter(t => existingKeys.has(`${t.id}-${t.type}`));
      this.preSelectedTemplates = [];
      this.selectedTemplates = [];
    }
  }

  private async loadTemplates(): Promise<void> {
    this.isLoading = true;
    try {
      const allTemplates = await this.apiService.getCopilotTemplates();
      this.processTemplates(allTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      this.templates = [];
    } finally {
      this.isLoading = false;
    }
  }

  onSelectionChange(templates: Template[]): void {
    this.selectedTemplates = templates;
  }

  // Fetch detail per selected template so workflow_trigger reflects the
  // authoritative settings.workflow_trigger value, matching Flutter behaviour.
  private async fetchFullTemplates(templates: Template[]): Promise<Template[]> {
    const results = await Promise.all(
      templates.map(t =>
        this.apiService
          .getTemplate(t.id.toString(), t.type)
          .catch(err => {
            console.error('Failed to fetch template detail', t.id, t.type, err);
            return null;
          })
      )
    );
    return results.filter((t): t is Template => t !== null);
  }

  async onConfirm(): Promise<void> {
    if (this.selectedTemplates.length === 0) {
      this.snackBar.open(this.translate.instant('Please select at least one blueprint.'), this.translate.instant('Close'), { duration: 3000 });
      return;
    }

    if (!this.dictation) {
      this.snackBar.open(this.translate.instant('Dictation not loaded.'), this.translate.instant('Close'), { duration: 3000 });
      return;
    }

    this.isSaving = true;
    try {
      const fullTemplates = await this.fetchFullTemplates(this.selectedTemplates);
      const mustProcess = hasDictationTranscription(this.dictation) || (this.dictation?.copilot?.notes?.length ?? 0) > 0;

      if (mustProcess) {
        // Check if user is offline
        if (!navigator.onLine) {
          // Show user-friendly message but still enqueue the templates
          this.snackBar.open('You are offline. Documents will be generated when internet connection is restored.', 'Close', { duration: 5000 });
        }
        
        // Has transcript: enqueue for AI processing (new documents use 'process' action)
        // This will handle offline scenarios with automatic retry when connection is restored
        this.processing.enqueue(this.dictation, fullTemplates, 'process');
        this.dialogRef.close({ templates: this.selectedTemplates, defaultDocumentsCreated: false });
      } else {
        // No transcript: create default notes with template structure
        await this.createDefaultNotes(fullTemplates);
        this.dialogRef.close({ templates: this.selectedTemplates, defaultDocumentsCreated: true });
      }
    } catch (error) {
      console.error('Error saving templates:', error);
      
      // Check if this is a network error
      const isNetworkError = !navigator.onLine || 
        (error as any)?.status === 0 || 
        (error as any)?.message?.toLowerCase().includes('network');
      
      if (isNetworkError) {
        this.snackBar.open('No internet connection. Please try again when online.', 'Close', { duration: 5000 });
      } else {
        this.snackBar.open('Failed to generate documents. Please try again.', 'Close', { duration: 3000 });
      }
    } finally {
      this.isSaving = false;
    }
  }

  private async createDefaultNotes(fullTemplates: Template[]): Promise<void> {
    if (!this.dictation) return;
    const documents: CopilotDocument[] = [];
    const existingDocs = this.dictation.copilot?.documents?.processed ?? [];
    let nextPosition = getNextDocumentPosition(existingDocs);

    for (const fullTemplate of fullTemplates) {
      // Clear deleted mark if document is being re-added (prevents ReplaySubject from blocking it)
      this.processing.clearDocumentDeleted(this.dictation._id, fullTemplate.id, fullTemplate.type);

      const sections = mapNoteSection((fullTemplate as any).sections);
      const documentSection = { 'BODY': { title: '', text: '' } };
      let text = '';

      // Match Flutter (tpro-copilot) behaviour: render only the section
      // titles with an empty body. Section content (including AI Blueprint
      // `instructions`) must NOT be printed into the default note.
      for (const section of Object.keys(sections)) {
        const sectionData = sections[section];
        if (sectionData.title) {
          text += `${sectionData.title}<br><br><br>`;
        }
      }

      documentSection['BODY'].text = text;

      const doc: CopilotDocument = {
        templateId: fullTemplate.id,
        templateType: fullTemplate.type,
        templateTitle: fullTemplate.title,
        sections: documentSection,
        error: false,
        rate: 0,
        workflow_trigger: fullTemplate.workflow_trigger,
        priority: 1,
        position: nextPosition++,
        _delete: false,
        approved: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      documents.push(doc);
    }

    // Update dictation with default note documents
    await this.apiService.updateDictationCopilot(this.dictation._id, {
      copilot: {
        documents: {
          processed: documents
        }
      }
    });

  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
